// web-frontend/src/shared/utils/userPermissions.js
// =============================================================================
// 사용자 타입 기반 권한 판정 — 온보딩 라우팅에 사용 (2026-05-21).
//
// 비즈니스 권한:
//   - COMPANY, BUYER, INVESTOR  → 풀 비즈니스 웹앱 사용 가능
//   - ADMIN, SUPER_ADMIN         → 풀 권한 + admin 콘솔 접근
//   - BUSINESS_VISITOR           → 승인 전: 큐레이션, 승인 후: 비즈니스 모드
//
// 큐레이션 (read-only):
//   - VISITOR, GUEST             → SPC 큐레이션 모드 (행사 정보 + 부스맵 + 챗봇)
//   - 미로그인                    → /login 으로 리다이렉트
//
// 설계 출처: 사용자 요청 (2026-05-21) — SPC 폴더 참고
// =============================================================================

export const UserTypes = {
  COMPANY: 'COMPANY',
  BUYER: 'BUYER',
  BUSINESS_VISITOR: 'BUSINESS_VISITOR',
  INVESTOR: 'INVESTOR',
  VISITOR: 'VISITOR',
  GUEST: 'GUEST',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
};

const BUSINESS_TYPES = new Set([
  UserTypes.COMPANY,
  UserTypes.BUYER,
  UserTypes.INVESTOR,
  UserTypes.ADMIN,
  UserTypes.SUPER_ADMIN,
]);

const CURATION_TYPES = new Set([
  UserTypes.VISITOR,
  UserTypes.GUEST,
]);

/**
 * 비즈니스 웹앱 (매칭/미팅/채팅 등) 사용 가능 여부.
 * BUSINESS_VISITOR 는 승인된 권한 플래그 기준으로 판단.
 */
export function isBusinessUser(user) {
  if (!user) return false;
  const t = String(user.user_type || '').toUpperCase();
  if (BUSINESS_TYPES.has(t)) return true;
  // BUSINESS_VISITOR: 둘 중 하나라도 승인되었으면 비즈니스 모드
  if (t === UserTypes.BUSINESS_VISITOR) {
    return Boolean(user.has_chat_permission || user.has_meeting_permission);
  }
  return false;
}

/**
 * 큐레이션 모드만 보여야 하는 사용자 (VISITOR/GUEST/승인 전 BUSINESS_VISITOR).
 */
export function isCurationOnlyUser(user) {
  if (!user) return false;
  const t = String(user.user_type || '').toUpperCase();
  if (CURATION_TYPES.has(t)) return true;
  if (t === UserTypes.BUSINESS_VISITOR && !isBusinessUser(user)) return true;
  return false;
}

// [2026-11-02] 데모·테스트 계정(@sample.dsk2027.kr) — 매년 유지, 비밀번호 고정.
// 백엔드 reset_company_passwords.py KEEP_EMAIL_DOMAINS 와 같은 도메인.
const DEMO_TEST_EMAIL_DOMAIN = '@sample.dsk2027.kr';

/**
 * 미팅 요청 시 전화번호·SMS 동의 확인을 건너뛰는 데모 테스트 계정 (VISITOR 제외).
 */
export function isDemoTestAccount(user) {
  if (!user?.email) return false;
  if (String(user.user_type || '').toUpperCase() === UserTypes.VISITOR) return false;
  return String(user.email).toLowerCase().endsWith(DEMO_TEST_EMAIL_DOMAIN);
}

/**
 * 로그인 직후 / 루트(/) 접근시 리다이렉트할 경로.
 */
export function getLandingPath(user) {
  if (!user) return '/login';
  // 2026-06-16: 모든 로그인 사용자의 홈은 비즈니스 HomePage. (큐레이션 자동 랜딩 제거 — 사용자 요청)
  return '/';
}

export default {
  UserTypes,
  isBusinessUser,
  isCurationOnlyUser,
  isDemoTestAccount,
  getLandingPath,
};
